import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { GET_CONFIG, UPDATE_CONFIG } from "../config/graphql";
import { Config } from "../../__generated__/graphql";
import { PageContainer } from "../../components/PageContainer";
import { LoadingOverlay } from "../../components/Loading";
import { MapTabs } from "./MapTabs";

const getVal = (configs: Config[], key: string) =>
  configs.find((c) => c.key === key)?.value ?? undefined;

const toBool = (val: string | undefined, defaultVal = false) =>
  val === undefined ? defaultVal : val === "true";

interface MapSettings {
  showStages: boolean;
  showFood: boolean;
  showBar: boolean;
  showBarsSeparately: boolean;
  showVendors: boolean;
  showMerch: boolean;
  showInfo: boolean;
  showInfoSeparately: boolean;
  showOther: boolean;
  showAccessibility: boolean;
  showCar: boolean;
  showTent: boolean;
  showcaseStages: boolean;
  showcaseName: string;
  showcaseIcon: string;
  showcaseItemsLabel: string;
  showcaseShowAreaLabels: boolean;
  showcaseShowFilters: boolean;
  showcaseViewArtistsLabel: string;
  vendorLabelMinZoom: string;
  gridCellSizeM: string;
  useImages: boolean;
  defaultSatelliteView: boolean;
}

const parseSettings = (configs: Config[]): MapSettings => ({
  showStages: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_STAGES")),
  showFood: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_FOOD")),
  showBar: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_BAR")),
  showBarsSeparately: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_BARS_SEPARATELY")),
  showVendors: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_VENDORS")),
  showMerch: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_MERCH")),
  showInfo: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_INFO")),
  showInfoSeparately: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_INFO_SEPARATELY")),
  showOther: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_OTHER")),
  showAccessibility: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_ACCESSIBILITY")),
  showCar: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_CAR")),
  showTent: toBool(getVal(configs, "INTERACTIVE_MAP_SHOW_TENT")),
  showcaseStages: toBool(getVal(configs, "INTERACTIVE_MAP_SHOWCASE_STAGES")),
  showcaseName: getVal(configs, "INTERACTIVE_MAP_SHOWCASE_STAGES_NAME") ?? "Showcase",
  showcaseIcon: getVal(configs, "INTERACTIVE_MAP_SHOWCASE_STAGES_ICON") ?? "music",
  showcaseItemsLabel: getVal(configs, "INTERACTIVE_MAP_SHOWCASE_STAGES_ITEMS_LABEL") ?? "",
  showcaseShowAreaLabels: toBool(getVal(configs, "INTERACTIVE_MAP_SHOWCASE_SHOW_AREA_LABELS")),
  showcaseShowFilters: toBool(getVal(configs, "INTERACTIVE_MAP_SHOWCASE_SHOW_FILTERS")),
  showcaseViewArtistsLabel: getVal(configs, "INTERACTIVE_MAP_SHOWCASE_VIEW_ARTISTS_LABEL") ?? "View artists",
  vendorLabelMinZoom: getVal(configs, "INTERACTIVE_MAP_VENDOR_LABEL_MIN_ZOOM") ?? "18",
  gridCellSizeM: getVal(configs, "INTERACTIVE_MAP_GRID_CELL_SIZE_M") ?? "100",
  useImages: toBool(getVal(configs, "INTERACTIVE_MAP_USE_IMAGES")),
  defaultSatelliteView: toBool(getVal(configs, "INTERACTIVE_MAP_DEFAULT_SATELLITE_VIEW")),
});

const b = (val: boolean) => (val ? "true" : "false");

interface MapSettingsPanelProps {
  tab: number;
  onTabChange: (tab: number) => void;
}

export const MapSettingsPanel = ({ tab, onTabChange }: MapSettingsPanelProps) => {
  const { data, loading } = useQuery(GET_CONFIG);
  const [updateConfig, { loading: saving }] = useMutation(UPDATE_CONFIG, {
    refetchQueries: ["config"],
    awaitRefetchQueries: true,
    onCompleted: () => toast("Map settings saved"),
  });

  const configs = (data?.configs as Config[]) ?? [];
  const [settings, setSettings] = useState<MapSettings>(() => parseSettings([]));

  useEffect(() => {
    if (!loading && data) {
      setSettings(parseSettings(configs));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  const set = <K extends keyof MapSettings>(key: K, value: MapSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    updateConfig({
      variables: {
        settings: [
          { key: "INTERACTIVE_MAP_SHOW_STAGES", value: b(settings.showStages) },
          { key: "INTERACTIVE_MAP_SHOW_FOOD", value: b(settings.showFood) },
          { key: "INTERACTIVE_MAP_SHOW_BAR", value: b(settings.showBar) },
          { key: "INTERACTIVE_MAP_SHOW_BARS_SEPARATELY", value: b(settings.showBarsSeparately) },
          { key: "INTERACTIVE_MAP_SHOW_VENDORS", value: b(settings.showVendors) },
          { key: "INTERACTIVE_MAP_SHOW_MERCH", value: b(settings.showMerch) },
          { key: "INTERACTIVE_MAP_SHOW_INFO", value: b(settings.showInfo) },
          { key: "INTERACTIVE_MAP_SHOW_INFO_SEPARATELY", value: b(settings.showInfoSeparately) },
          { key: "INTERACTIVE_MAP_SHOW_OTHER", value: b(settings.showOther) },
          { key: "INTERACTIVE_MAP_SHOW_ACCESSIBILITY", value: b(settings.showAccessibility) },
          { key: "INTERACTIVE_MAP_SHOW_CAR", value: b(settings.showCar) },
          { key: "INTERACTIVE_MAP_SHOW_TENT", value: b(settings.showTent) },
          { key: "INTERACTIVE_MAP_SHOWCASE_STAGES", value: b(settings.showcaseStages) },
          { key: "INTERACTIVE_MAP_SHOWCASE_STAGES_NAME", value: settings.showcaseName },
          { key: "INTERACTIVE_MAP_SHOWCASE_STAGES_ICON", value: settings.showcaseIcon },
          { key: "INTERACTIVE_MAP_SHOWCASE_STAGES_ITEMS_LABEL", value: settings.showcaseItemsLabel },
          { key: "INTERACTIVE_MAP_SHOWCASE_SHOW_AREA_LABELS", value: b(settings.showcaseShowAreaLabels) },
          { key: "INTERACTIVE_MAP_SHOWCASE_SHOW_FILTERS", value: b(settings.showcaseShowFilters) },
          { key: "INTERACTIVE_MAP_SHOWCASE_VIEW_ARTISTS_LABEL", value: settings.showcaseViewArtistsLabel },
          { key: "INTERACTIVE_MAP_VENDOR_LABEL_MIN_ZOOM", value: settings.vendorLabelMinZoom },
          { key: "INTERACTIVE_MAP_GRID_CELL_SIZE_M", value: settings.gridCellSizeM },
          { key: "INTERACTIVE_MAP_USE_IMAGES", value: b(settings.useImages) },
          { key: "INTERACTIVE_MAP_DEFAULT_SATELLITE_VIEW", value: b(settings.defaultSatelliteView) },
        ],
      },
    });
  };

  return (
    <PageContainer
      title="Map Settings"
      actions={
        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? "Saving…" : "Save settings"}
        </Button>
      }
    >
      <MapTabs value={tab} onChange={onTabChange} />
      <LoadingOverlay loading={loading}>
        <Box sx={{ maxWidth: 600 }}>

          <Typography variant="h6" sx={{ mb: 1 }}>Visible categories</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which types of markers appear on the map.
          </Typography>
          <FormGroup>
            <FormControlLabel
              label="Stages"
              control={<Switch checked={settings.showStages} onChange={(e) => set("showStages", e.target.checked)} />}
            />
            <FormControlLabel
              label="Food vendors"
              control={<Switch checked={settings.showFood} onChange={(e) => set("showFood", e.target.checked)} />}
            />
            <FormControlLabel
              sx={{ ml: 4 }}
              label="Bars as separate category (when off, bars merge into Food)"
              control={
                <Switch
                  checked={settings.showBarsSeparately}
                  disabled={!settings.showBar}
                  onChange={(e) => set("showBarsSeparately", e.target.checked)}
                />
              }
            />
            <FormControlLabel
              label="Bars"
              control={<Switch checked={settings.showBar} onChange={(e) => set("showBar", e.target.checked)} />}
            />
            <FormControlLabel
              label="General vendors"
              control={<Switch checked={settings.showVendors} onChange={(e) => set("showVendors", e.target.checked)} />}
            />
            <FormControlLabel
              label="Merchandise"
              control={<Switch checked={settings.showMerch} onChange={(e) => set("showMerch", e.target.checked)} />}
            />
            <FormControlLabel
              label="Info points"
              control={<Switch checked={settings.showInfo} onChange={(e) => set("showInfo", e.target.checked)} />}
            />
            <FormControlLabel
              sx={{ ml: 4 }}
              label="Info as separate category (when off, info merges into Other)"
              control={
                <Switch
                  checked={settings.showInfoSeparately}
                  disabled={!settings.showInfo}
                  onChange={(e) => set("showInfoSeparately", e.target.checked)}
                />
              }
            />
            <FormControlLabel
              label="Other / miscellaneous"
              control={<Switch checked={settings.showOther} onChange={(e) => set("showOther", e.target.checked)} />}
            />
            <FormControlLabel
              label="Accessibility points"
              control={<Switch checked={settings.showAccessibility} onChange={(e) => set("showAccessibility", e.target.checked)} />}
            />
            <FormControlLabel
              label="Car marker button"
              control={<Switch checked={settings.showCar} onChange={(e) => set("showCar", e.target.checked)} />}
            />
            <FormControlLabel
              label="Tent marker button"
              control={<Switch checked={settings.showTent} onChange={(e) => set("showTent", e.target.checked)} />}
            />
          </FormGroup>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>Showcase mode</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            An alternative to regular stages — shows a custom category backed by a different data source.
          </Typography>
          <FormGroup>
            <FormControlLabel
              label="Enable showcase stages"
              control={<Switch checked={settings.showcaseStages} onChange={(e) => set("showcaseStages", e.target.checked)} />}
            />
            <FormControlLabel
              label="Show area labels on map"
              control={<Switch checked={settings.showcaseShowAreaLabels} onChange={(e) => set("showcaseShowAreaLabels", e.target.checked)} />}
            />
            <FormControlLabel
              label="Show tag filter chips"
              control={<Switch checked={settings.showcaseShowFilters} onChange={(e) => set("showcaseShowFilters", e.target.checked)} />}
            />
          </FormGroup>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Category name"
              value={settings.showcaseName}
              disabled={!settings.showcaseStages}
              onChange={(e) => set("showcaseName", e.target.value)}
              helperText="Label shown in the app for this category"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Icon name"
              value={settings.showcaseIcon}
              disabled={!settings.showcaseStages}
              onChange={(e) => set("showcaseIcon", e.target.value)}
              helperText='Icon identifier, e.g. "music", "star"'
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Items label (optional)"
              value={settings.showcaseItemsLabel}
              disabled={!settings.showcaseStages}
              onChange={(e) => set("showcaseItemsLabel", e.target.value)}
              helperText="Label shown above the list of showcase items"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="View artists button label"
              value={settings.showcaseViewArtistsLabel}
              disabled={!settings.showcaseStages}
              onChange={(e) => set("showcaseViewArtistsLabel", e.target.value)}
              helperText='Button label shown on a showcase stage, e.g. "View artists"'
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 1 }}>Advanced</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Min zoom for vendor labels"
              type="number"
              value={settings.vendorLabelMinZoom}
              onChange={(e) => set("vendorLabelMinZoom", e.target.value)}
              inputProps={{ min: 13, max: 20, step: 1 }}
              helperText="Zoom level at which vendor name labels appear (13–20, default 18)"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 280 }}
            />
            <TextField
              label="Staff grid cell size (metres)"
              type="number"
              value={settings.gridCellSizeM}
              onChange={(e) => set("gridCellSizeM", e.target.value)}
              inputProps={{ min: 10, max: 500, step: 1 }}
              helperText="Size of each cell in the staff grid overlay (default 100)"
              InputLabelProps={{ shrink: true }}
              sx={{ maxWidth: 280 }}
            />
            <FormGroup>
              <FormControlLabel
                label="Use raster images instead of SVG icons"
                control={<Switch checked={settings.useImages} onChange={(e) => set("useImages", e.target.checked)} />}
              />
              <FormControlLabel
                label="Default to satellite view"
                control={<Switch checked={settings.defaultSatelliteView} onChange={(e) => set("defaultSatelliteView", e.target.checked)} />}
              />
            </FormGroup>
          </Box>

        </Box>
      </LoadingOverlay>
    </PageContainer>
  );
};
